import { z } from "zod";

/** Role atribuível via convite de equipe — nunca OWNER (só existe 1 por organização, via onboarding/transfer). */
/** `PARTNER` (Sócio) só pode ser dado pelo proprietário, com PIN — conferido no servidor. */
export const inviteRoleSchema = z.enum(["PARTNER", "ADMIN", "DENTIST", "ASSISTANT", "RECEPTIONIST", "VIEWER"]);
export type InviteRole = z.infer<typeof inviteRoleSchema>;

export const teamInviteStatusSchema = z.enum([
  "PENDING",
  "ACCEPTED",
  "REVOKED",
  "EXPIRED",
]);
export type TeamInviteStatus = z.infer<typeof teamInviteStatusSchema>;

export const membershipStatusSchema = z.enum(["ACTIVE", "SUSPENDED"]);
export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

export const teamInviteCreateSchema = z.object({
  email: z.string().trim().toLowerCase().email("Email inválido"),
  role: inviteRoleSchema,
  expiresInDays: z.number().int().min(1).max(30).default(7),
});
export type TeamInviteCreateInput = z.infer<typeof teamInviteCreateSchema>;

/** Convite direto pra um papel customizado (clone com nome próprio) — só OWNER, ver
 *  `organization.customRoles.*`. `role` do convite é derivado de `customRole.basedOnRole` no
 *  backend, nunca escolhido aqui. */
export const teamInviteCreateForCustomRoleSchema = z.object({
  email: z.string().trim().toLowerCase().email("Email inválido"),
  customRoleId: z.string().min(1),
  expiresInDays: z.number().int().min(1).max(30).default(7),
});
export type TeamInviteCreateForCustomRoleInput = z.infer<typeof teamInviteCreateForCustomRoleSchema>;

/** Um convite pra várias organizações: o acesso é escolhido por organização. */
export const teamInviteEmVariasSchema = z.object({
  email: z.string().trim().toLowerCase().email("Email inválido"),
  organizacoes: z
    .array(
      z
        .object({
          organizationId: z.string().min(1),
          role: inviteRoleSchema.optional(),
          customRoleId: z.string().min(1).optional(),
        })
        .refine((o) => !!o.role !== !!o.customRoleId, { message: "Escolha o acesso de cada organização." }),
    )
    .min(1)
    .max(20),
  expiresInDays: z.number().int().min(1).max(30).default(7),
});
export type TeamInviteEmVariasInput = z.infer<typeof teamInviteEmVariasSchema>;

export const teamInviteAcceptSchema = z.object({
  token: z.string().min(1),
  /** Convite de grupo: `true` aceita só esta organização e deixa as outras pendentes. */
  apenasEste: z.boolean().optional(),
});
export type TeamInviteAcceptInput = z.infer<typeof teamInviteAcceptSchema>;

export const updateMemberRoleSchema = z.object({
  membershipId: z.string().min(1),
  role: inviteRoleSchema,
});
export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleSchema>;

export const memberIdInputSchema = z.object({ membershipId: z.string().min(1) });
export type MemberIdInput = z.infer<typeof memberIdInputSchema>;

/** Lista de ProcedureType (Organization.agendaSettings.procedureTypes) atribuídos a um membro —
 *  gestão manual pelo dono, ver issue #8. */
export const updateAssignedProceduresSchema = z.object({
  membershipId: z.string().min(1),
  procedureTypeIds: z.array(z.string().min(1)),
});
export type UpdateAssignedProceduresInput = z.infer<typeof updateAssignedProceduresSchema>;

/** Atribui um papel customizado (clone) a um membro já existente — só OWNER. `role` estrutural do
 *  membro passa a refletir `customRole.basedOnRole` no backend (nunca escolhido aqui). */
export const assignCustomRoleSchema = z.object({
  membershipId: z.string().min(1),
  customRoleId: z.string().min(1),
});
export type AssignCustomRoleInput = z.infer<typeof assignCustomRoleSchema>;

/** Inicia a transferência de propriedade — gera código de 6 dígitos enviado pro e-mail do OWNER atual. */
export const transferOwnershipSchema = z.object({
  toMembershipId: z.string().min(1),
});
export type TransferOwnershipInput = z.infer<typeof transferOwnershipSchema>;

/** Confirma a transferência de propriedade com o código de 6 dígitos recebido por e-mail. */
export const confirmOwnershipTransferSchema = z.object({
  requestId: z.string().min(1),
  code: z.string().length(6).regex(/^\d{6}$/, "Código deve ter 6 dígitos"),
});
export type ConfirmOwnershipTransferInput = z.infer<typeof confirmOwnershipTransferSchema>;

/**
 * Convite em lote — mesma organização, mesmo papel, vários e-mails.
 *
 * Papel único pro lote de propósito: papel por linha dentro de uma caixa de texto vira sintaxe que
 * ninguém acerta de primeira ("ana@x.com, DENTIST"). Quem precisa de papéis diferentes manda dois
 * lotes, que é mais trabalho e menos erro.
 *
 * Teto de 25: é o que cabe numa clínica de verdade convidando a equipe inteira de uma vez, e
 * segura o custo de disparar e-mail para endereço digitado errado em massa.
 */
export const teamInviteCreateManySchema = z
  .object({
    emails: z.array(z.string().trim().toLowerCase().email("Email inválido")).min(1).max(25),
    role: inviteRoleSchema.optional(),
    customRoleId: z.string().min(1).optional(),
    expiresInDays: z.number().int().min(1).max(30).default(7),
  })
  .refine((v) => !!v.role !== !!v.customRoleId, {
    message: "Escolha um papel-base OU um papel customizado.",
  });
export type TeamInviteCreateManyInput = z.infer<typeof teamInviteCreateManySchema>;

/** O que aconteceu com cada e-mail do lote — a tela mostra agrupado por este motivo. */
export type ResultadoDoConviteEmLote = {
  email: string;
  situacao: "enviado" | "ja_membro" | "ja_convidado" | "erro";
  detalhe?: string;
};
